import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthStateService } from '../services/auth-state/auth-state.service';

export const errorInterceptor = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const messageService = inject(MessageService);
  const authState = inject(AuthStateService);
  const router = inject(Router);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      let errorMessage = 'An unknown error occurred';
      let errorTitle = 'Error';

      // Handle different error types
      if (error.error instanceof ErrorEvent) {
        // Client-side error
        errorTitle = 'Connection Problem';
        errorMessage = `Client Error: ${error.error.error.message}`;
      } else {
        // Server-side error
        switch (error.status) {
          case 0:
            errorTitle = 'No Internet';
            errorMessage =
              'Please check your internet connection and try again.';
            break;
          case 400:
            errorTitle = 'Bad Request';
            console.log(error);
            errorMessage =
              error?.message + error?.error?.error?.message ||
              'Invalid request. Please check your input.';
            break;
          case 401:
            errorTitle = 'Session Expired';
            errorMessage = 'Your session has expired. Please log in again.';
            // Auto logout
            authState.logout();
            router.navigate(['/login']);
            break;
          case 403:
            errorTitle = 'Access Denied';
            errorMessage = 'You do not have permission to perform this action.';
            break;
          case 404:
            errorTitle = 'Not Found';
            errorMessage = 'The requested resource was not found.';
            break;
          case 408:
            errorTitle = 'Request Timeout';
            errorMessage = 'The server took too long to respond.';
            break;
          case 422:
            errorTitle = 'Validation Failed';
            errorMessage = Object.values(error.error?.errors || {})
              .flat()
              .join(', ');
            break;
          case 500:
            errorTitle = 'Server Error';
            errorMessage =
              'Something went wrong on the server. Please try again later.';
            break;
          case 502:
          case 503:
          case 504:
            errorTitle = 'Service Unavailable';
            errorMessage =
              'The server is temporarily unavailable. Please try again later.';
            break;
          default:
            errorTitle = `Error ${error.status}`;
            errorMessage =
              error.error?.error?.message ||
              error.message ||
              'An unexpected error occurred.';
        }
      }

      // Show toast (skip for 401 to avoid double message)
      if (error.status !== 401) {
        messageService.add({
          key: 'global',
          severity: 'error',
          summary: errorTitle,
          detail: errorMessage,
          life: error.status === 401 ? 3000 : 8000,
          id: 'toast_' + Date.now() + '_' + Math.random(),
        });
      }

      // Re-throw error so components can still handle if needed
      return throwError(() => error);
    })
  );
};
